///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { Component, input, OnDestroy, OnInit } from '@angular/core';
import { FormGroup, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { merge, Subscription } from 'rxjs';
import { CcmIntervalEnum, ExportLinkConfigDTO, PlannerFeatureCollectionDTO } from '../../../../generated/shared-types.model';
import { GtthReactiveTextbox } from '../../../../shared/reactive-forms/input-text';
import { GtthInputControlService } from '../../../../shared/reactive-forms/input-control.service';
import { GtthReactiveNumber } from '../../../../shared/reactive-forms/input-number';
import { GtthReactiveDropdown } from '../../../../shared/reactive-forms/input-dropdown';
import { parseFormDataToObj } from '../../../../shared/utils';
import { SuiToggleControlComponent, SuiTypeToggle } from '@siklu/sui-lib/infrastructure';
import { GtthReactiveFormInputComponent } from '../../../../shared/reactive-forms/reactive-form-input.component';
import { IpIsNotMulticastValidation } from '../../../../shared/directives/validation-functions/ip-is-not-multicast-validation';
import { GtthDefaultGatewaySubnetValidation } from '../../../../shared/directives/validation-functions/gw-subnet-validator';
import { GtthEntityNameValidation } from '../../../../shared/directives/validation-functions/entity-name';
import { GtthSNMPVersionEnum } from '../../../../services/logical-topology/snmp-version';
import { GtthDisableFormControlsDirective } from '../../../../shared/directives/validation-directives/disable-form-controls.directive';

@Component({
    selector: 'winde-project-dialog-nw-config-tab',
    templateUrl: 'project-dialog-nw-config-tab.component.html',
    styleUrls: ['../../../projects.scss'],
    imports: [ReactiveFormsModule, SuiToggleControlComponent, GtthReactiveFormInputComponent, GtthDisableFormControlsDirective]
})
export class ProjectDialogNwConfigTabComponent implements OnInit, OnDestroy {
    viewInReadOnly = input<boolean>();
    logicalConfig = input<PlannerFeatureCollectionDTO>();

    public editForm: FormGroup;

    public startIpInput: GtthReactiveTextbox;
    public defaultGatewayInput: GtthReactiveTextbox;
    public subnetMaskInput: GtthReactiveNumber;
    public managementVidInput: GtthReactiveNumber;
    public snmpVersionInput: GtthReactiveDropdown;
    public snmpIPInput: GtthReactiveTextbox;
    public udpPortInput: GtthReactiveNumber;
    public trapInput: GtthReactiveTextbox;
    public engineIdInput: GtthReactiveTextbox;
    public ssidInput: GtthReactiveTextbox;
    public ssidpwInput: GtthReactiveTextbox;
    public inputMngUname: GtthReactiveTextbox;
    public inputMngUpass: GtthReactiveTextbox;
    public ipLessTuToggle: SuiTypeToggle;
    public ccmIntervalInput: GtthReactiveDropdown;

    private subnetFieldsSubscriber: Subscription;

    constructor(private readonly inputControlService: GtthInputControlService) {}

    static getIpValidators(): ValidatorFn[] {
        return [
            Validators.pattern(
                /^(25[0-5]|2[0-4]\d|[01]?\d\d?)\.(25[0-5]|2[0-4]\d|[01]?\d\d?)\.(25[0-5]|2[0-4]\d|[01]?\d\d?)\.(25[0-5]|2[0-4]\d|[01]?\d\d?)$/
            ),
            IpIsNotMulticastValidation()
        ];
    }

    static getDefaultGatewayValidator(isRequired: boolean, subnetMask: number, startIp: string): ValidatorFn {
        const validators = ProjectDialogNwConfigTabComponent.getIpValidators();
        if (isRequired) {
            validators.push(Validators.required);
        }
        validators.push(GtthDefaultGatewaySubnetValidation(subnetMask, startIp));
        return Validators.compose(validators);
    }

    static getSubnetMaskValidators(): ValidatorFn[] {
        return [Validators.min(0), Validators.max(32)];
    }

    static getStartIpField(isRequired: boolean): GtthReactiveTextbox {
        const validtors = ProjectDialogNwConfigTabComponent.getIpValidators();
        if (isRequired) {
            validtors.push(Validators.required);
        }

        return new GtthReactiveTextbox({
            label: 'Start IP address',
            key: 'startIp',
            inputClass: 'form-control',
            validationFn: Validators.compose(validtors)
        } as GtthReactiveTextbox);
    }

    static getSubnetMaskField(isRequired: boolean): GtthReactiveNumber {
        const validtors = ProjectDialogNwConfigTabComponent.getSubnetMaskValidators();
        if (isRequired) {
            validtors.push(Validators.required);
        }
        return new GtthReactiveNumber({
            label: 'Subnet mask width',
            key: 'subnetMask',
            inputClass: 'form-control',
            min: 0,
            max: 32,
            step: 1,
            validationFn: validtors
        } as GtthReactiveNumber);
    }

    static getDefaultGatewayField(isRequired: boolean, exportLinkConfig: ExportLinkConfigDTO): GtthReactiveTextbox {
        const validators = ProjectDialogNwConfigTabComponent.getDefaultGatewayValidator(
            isRequired,
            exportLinkConfig.subnetMask,
            exportLinkConfig.startIp
        );

        return new GtthReactiveTextbox({
            label: 'Default GW IP',
            key: 'defaultGateway',
            inputClass: 'form-control',
            validationFn: validators
        } as GtthReactiveTextbox);
    }

    static getManagementVidField(): GtthReactiveNumber {
        return new GtthReactiveNumber({
            label: 'Management VLAN',
            key: 'managementVid',
            inputClass: 'form-control',
            min: 1,
            max: 4094,
            step: 1,

            validationFn: Validators.compose([Validators.min(0), Validators.max(4094)])
        } as GtthReactiveNumber);
    }

    static getSnmpVersionField(): GtthReactiveDropdown {
        const snmpVersionOptions: any[] = [GtthSNMPVersionEnum.V2C, GtthSNMPVersionEnum.V3].map((index) => {
            return { versionIndex: index, versionName: GtthSNMPVersionEnum[index] };
        });

        return new GtthReactiveDropdown({
            label: 'SNMP Version',
            key: 'snmpVersion',
            inputClass: 'form-control',
            showKey: 'versionName',
            valueKey: 'versionIndex',
            options: snmpVersionOptions || []
        } as GtthReactiveDropdown);
    }

    static getSnmpIpField(): GtthReactiveTextbox {
        return new GtthReactiveTextbox({
            label: 'IP address',
            key: 'snmpIP',
            inputClass: 'form-control',
            validationFn: ProjectDialogNwConfigTabComponent.getIpValidators()
        } as GtthReactiveTextbox);
    }

    static getUdpPortField(): GtthReactiveNumber {
        return new GtthReactiveNumber({
            label: 'UDP-Port',
            key: 'udpPort',
            inputClass: 'form-control',
            min: 0,
            max: 64000,
            step: 1,
            validationFn: Validators.compose([Validators.min(0), Validators.max(64000)])
        } as GtthReactiveNumber);
    }

    static getTrapField(): GtthReactiveTextbox {
        return new GtthReactiveTextbox({
            label: 'Trap Community',
            key: 'trap',
            inputClass: 'form-control'
        } as GtthReactiveTextbox);
    }

    static getEngineIdField(): GtthReactiveTextbox {
        return new GtthReactiveTextbox({
            label: 'Engine ID',
            key: 'engineId',
            inputClass: 'form-control'
        } as GtthReactiveTextbox);
    }

    static getSsidField(): GtthReactiveTextbox {
        return new GtthReactiveTextbox({
            label: 'SSID',
            key: 'ssid',
            inputClass: 'form-control',
            maxLength: 250,
            validationFn: [Validators.maxLength(250), GtthEntityNameValidation(/[^A-Za-z\d\s#@!;$&%()_+=.,~\-{}[\]^'`]/g)]
        } as GtthReactiveTextbox);
    }

    static getSsidPasswordField(): GtthReactiveTextbox {
        return new GtthReactiveTextbox({
            label: 'SSID Password',
            key: 'ssidpw',
            inputClass: 'form-control',
            maxLength: 250,
            validationFn: Validators.compose([
                Validators.maxLength(250),
                GtthEntityNameValidation(/[^A-Za-z\d\s#@!;$&%()_+=.,~\-{}[\]^'`]/g)
            ])
        } as GtthReactiveTextbox);
    }

    static getMngUsernameField(): GtthReactiveTextbox {
        return new GtthReactiveTextbox({
            label: 'SSH user name',
            key: 'mng_uname',
            inputClass: 'form-control',
            maxLength: 250,
            validationFn: Validators.compose([
                Validators.maxLength(250),
                GtthEntityNameValidation(/[^A-Za-z\d\s#@!;$&%()_+=.,~\-{}[\]^'`]/g)
            ])
        } as GtthReactiveTextbox);
    }

    static getMngPasswordField(): GtthReactiveTextbox {
        return new GtthReactiveTextbox({
            label: 'SSH user password',
            key: 'mng_upass',
            inputClass: 'form-control',
            maxLength: 250,
            validationFn: Validators.compose([
                Validators.maxLength(250),
                GtthEntityNameValidation(/[^A-Za-z\d\s#@!;$&%()_+=.,~\-{}[\]^'`]/g)
            ])
        } as GtthReactiveTextbox);
    }

    static getIpLessTuField(value: boolean, readonly: boolean): SuiTypeToggle {
        const config = new SuiTypeToggle(value);
        config.applyModifiedStateStyle = false;
        config.readonly = readonly;
        return config;
    }

    static getCcmIntervalField(): GtthReactiveDropdown {
        const ccmIntervalOptions: any[] = [
            CcmIntervalEnum._10MS,
            CcmIntervalEnum._100MS,
            CcmIntervalEnum._1SEC,
            CcmIntervalEnum._10SEC,
            CcmIntervalEnum._1MIN,
            CcmIntervalEnum._10MIN
        ];

        return new GtthReactiveDropdown({
            label: 'CCM Interval',
            key: 'ccmInterval',
            inputClass: 'form-control',
            simpleArr: true,
            options: ccmIntervalOptions || []
        } as GtthReactiveDropdown);
    }

    ngOnInit(): void {
        this.initForm();

        this.subnetFieldsSubscriber = merge(
            this.inputControlService.fieldValueChange(this.editForm, this.subnetMaskInput),
            this.inputControlService.fieldValueChange(this.editForm, this.startIpInput)
        ).subscribe(() => {
            const maskInput = this.inputControlService.getInputControl(this.editForm, this.subnetMaskInput);
            const startIpInput = this.inputControlService.getInputControl(this.editForm, this.startIpInput);

            if (startIpInput.valid && maskInput.valid) {
                this.defaultGatewayInput.validationFn = ProjectDialogNwConfigTabComponent.getDefaultGatewayValidator(
                    this.defaultGatewayInput.required,
                    maskInput.value,
                    startIpInput.value
                );
                this.inputControlService.updateInputValidation(this.editForm, this.defaultGatewayInput);
                this.inputControlService.inputChangeEventBroadcast(this.defaultGatewayInput);
            }
        });
    }

    public validateAndApply(): boolean {
        if (this.editForm.invalid) {
            this.inputControlService.formSubmitEventBroadcast(this.editForm);
            return false;
        }

        parseFormDataToObj(this.logicalConfig().crs.properties.saved_exportLinksConfig, this.editForm);
        this.logicalConfig().crs.properties.saved_exportLinksConfig.ipLessTu = this.ipLessTuToggle.getValue();

        return true;
    }

    ngOnDestroy(): void {
        if (this.subnetFieldsSubscriber) {
            this.subnetFieldsSubscriber.unsubscribe();
            this.subnetFieldsSubscriber = null;
        }
    }

    private initForm() {
        this.startIpInput = ProjectDialogNwConfigTabComponent.getStartIpField(false);
        this.startIpInput.value = this.logicalConfig().crs.properties.saved_exportLinksConfig.startIp;

        this.subnetMaskInput = ProjectDialogNwConfigTabComponent.getSubnetMaskField(false);
        this.subnetMaskInput.value = this.logicalConfig().crs.properties.saved_exportLinksConfig.subnetMask;

        this.defaultGatewayInput = ProjectDialogNwConfigTabComponent.getDefaultGatewayField(
            false,
            this.logicalConfig().crs.properties.saved_exportLinksConfig
        );
        this.defaultGatewayInput.value = this.logicalConfig().crs.properties.saved_exportLinksConfig.defaultGateway;

        this.managementVidInput = ProjectDialogNwConfigTabComponent.getManagementVidField();
        this.managementVidInput.value = this.logicalConfig().crs.properties.saved_exportLinksConfig.managementVid;

        this.snmpVersionInput = ProjectDialogNwConfigTabComponent.getSnmpVersionField();
        this.snmpVersionInput.value = this.logicalConfig().crs.properties.saved_exportLinksConfig.snmpVersion;

        this.snmpIPInput = ProjectDialogNwConfigTabComponent.getSnmpIpField();
        this.snmpIPInput.value = this.logicalConfig().crs.properties.saved_exportLinksConfig.snmpIP;

        this.udpPortInput = ProjectDialogNwConfigTabComponent.getUdpPortField();
        this.udpPortInput.value = this.logicalConfig().crs.properties.saved_exportLinksConfig.udpPort;

        this.trapInput = ProjectDialogNwConfigTabComponent.getTrapField();
        this.trapInput.value = this.logicalConfig().crs.properties.saved_exportLinksConfig.trap;

        this.engineIdInput = ProjectDialogNwConfigTabComponent.getEngineIdField();
        this.engineIdInput.value = this.logicalConfig().crs.properties.saved_exportLinksConfig.engineId;

        this.ssidInput = ProjectDialogNwConfigTabComponent.getSsidField();
        this.ssidInput.value = this.logicalConfig().crs.properties.saved_exportLinksConfig.ssid;

        this.ssidpwInput = ProjectDialogNwConfigTabComponent.getSsidPasswordField();
        this.ssidpwInput.value = this.logicalConfig().crs.properties.saved_exportLinksConfig.ssidpw;

        this.inputMngUname = ProjectDialogNwConfigTabComponent.getMngUsernameField();
        this.inputMngUname.value = this.logicalConfig().crs.properties.saved_exportLinksConfig.mng_uname;

        this.inputMngUpass = ProjectDialogNwConfigTabComponent.getMngPasswordField();
        this.inputMngUpass.value = this.logicalConfig().crs.properties.saved_exportLinksConfig.mng_upass;

        this.ipLessTuToggle = ProjectDialogNwConfigTabComponent.getIpLessTuField(
            this.logicalConfig().crs.properties.saved_exportLinksConfig.ipLessTu,
            this.viewInReadOnly()
        );

        this.ccmIntervalInput = ProjectDialogNwConfigTabComponent.getCcmIntervalField();
        this.ccmIntervalInput.value = this.logicalConfig().crs.properties.saved_exportLinksConfig.ccmInterval;

        this.editForm = this.inputControlService.toFormGroup([
            this.startIpInput,
            this.subnetMaskInput,
            this.defaultGatewayInput,
            this.managementVidInput,
            this.snmpVersionInput,
            this.snmpIPInput,
            this.udpPortInput,
            this.trapInput,
            this.engineIdInput,
            this.ssidInput,
            this.ssidpwInput,
            this.inputMngUname,
            this.inputMngUpass,
            this.ccmIntervalInput
        ]);
    }
}
